import React from 'react';
import styled from 'styled-components';
import { Height, TrevorComponent, Width } from '../../../types';
import { useDropdown } from '../DropdownHooks';
import ReactDOM from 'react-dom';

const BOUNDING_CLIENT_RECT_PLACEHOLDER = {
  top: 0,
  left: 0,
  height: 0,
};

const bodyEl = document.querySelector('body')!;
const overlayEl = document.createElement('div');
overlayEl.style.position = 'fixed';
overlayEl.style.zIndex = '10';
overlayEl.style.top = '0';
overlayEl.style.left = '0';

export type ContentTypeResolverProps = {
  top?: number;
  left?: number;
  isFixed?: boolean;
  width?: Width;
};

export type ContentProps = Omit<ContentTypeResolverProps, 'isFixed'>;

const FixedContent: TrevorComponent<ContentProps> = ({
  className,
  children,
}) => {
  const { isOpen } = useDropdown();

  return isOpen ? <div className={className}>{children}</div> : null;
};

const FloatingContent: TrevorComponent<ContentProps> = ({
  top,
  left = 0,
  children,
  className,
}) => {
  const { isOpen, triggerRef, contentRef } = useDropdown();
  const isOpenRef = React.useRef<typeof isOpen>();

  React.useEffect(() => {
    if (isOpen) {
      bodyEl.appendChild(overlayEl);
      isOpenRef.current = true;
    } else {
      if (isOpenRef.current !== undefined) {
        bodyEl.removeChild(overlayEl);
        isOpenRef.current = false;
      }
    }
  }, [isOpen]);

  const {
    top: triggerTop,
    left: triggerLeft,
    height: triggerHeight,
  } = triggerRef.current?.getBoundingClientRect() ??
  BOUNDING_CLIENT_RECT_PLACEHOLDER;

  return isOpen
    ? ReactDOM.createPortal(
        <div
          className={className}
          ref={contentRef}
          style={{
            top: triggerTop + (top ?? triggerHeight),
            left: triggerLeft + left,
          }}
        >
          {children}
        </div>,
        overlayEl,
      )
    : null;
};

const ContentTypeResolver: TrevorComponent<ContentTypeResolverProps> = ({
  isFixed = true,
  ...rest
}) => (isFixed ? <FixedContent {...rest} /> : <FloatingContent {...rest} />);

export const Content = styled(ContentTypeResolver)`
  width: ${(p) => p.width || 'max-content'};
  position: absolute;
  top: ${(p) => p.top || 0}px;
  left: ${(p) => p.left || 0}px;
  z-index: 1;
`;
